import { StandardEvents } from '@shopify/events';

/**
 * <add-to-cart-modal> — the "Product added to cart" popup (Figma 1529-118354).
 *
 * Reuses the theme's cart event backbone: it listens for
 * `StandardEvents.cartLinesUpdate` (action `add`) on `document`, then fills the
 * static modal body from the resolved event payload (`detail.items` already
 * carries title, description, image, price and quantity — no extra request) and
 * opens a native <dialog>.
 *
 * Behaviour is gated by `data-behavior`:
 *   - 'modal'        → opens on add (default)
 *   - 'counter-only' → never opens (the nav counter still updates on its own)
 *
 * The quantity stepper edits the just-added cart line through the existing
 * <cart-items-component>.updateQuantity(), so the drawer, totals and nav
 * counter all stay in sync. "Go to My Cart" opens <theme-drawer id="cart-drawer">.
 */
class AddToCartModal extends HTMLElement {
  /** @type {number | null} 1-based cart line index of the added item */
  #lineIndex = null;

  /** @type {string} variant id of the tracked line, used to re-find it after quantity edits */
  #trackedVariantId = '';

  connectedCallback() {
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);

    const dialog = this.#dialog;
    dialog?.addEventListener('click', (event) => {
      if (event.target === dialog) this.#close();
    });

    this.querySelector('[data-at-cart-close]')?.addEventListener('click', () => this.#close());
    this.querySelector('.at-btn--p-accent')?.addEventListener('click', () => {
      this.#close();
      this.#openCartDrawer();
    });
    this.querySelector('.at-btn--p-dark')?.addEventListener('click', () => this.#close());

    // The branded stepper (rendered statically) dispatches `at:quantity-change`;
    // relay it to the just-added cart line so everything stays in sync.
    this.querySelector('.at-btn--number')?.addEventListener('at:quantity-change', (event) => {
      const quantity = Number(/** @type {CustomEvent} */ (event).detail?.value);
      if (Number.isFinite(quantity)) this.#setLineQuantity(quantity);
    });
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
  }

  /** @returns {HTMLDialogElement | null} */
  get #dialog() {
    return this.querySelector('dialog');
  }

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    // Quantity edits from the modal's own stepper come back as 'update' (or
    // 'remove' when the count hits 0), never 'add'. While the modal is open,
    // keep the "Go to My Cart (n)" count and the line price in sync — without
    // re-opening the modal.
    if (event.action === 'update' || event.action === 'remove') {
      if (!this.#dialog?.open) return;
      event.promise
        ?.then(({ cart, detail }) => {
          if (!detail?.didError) this.#syncTotals({ cart, detail });
        })
        .catch((error) => {
          if (error?.name !== 'AbortError') console.warn('[add-to-cart-modal] event promise rejected:', error);
        });
      return;
    }

    if (event.action !== 'add' || this.dataset.behavior !== 'modal') return;

    const addedVariantId = String(event.lines?.[0]?.merchandiseId ?? '');

    // If the add originated inside another modal dialog (e.g. quick-add), wait
    // for it to close first so focus restoration happens in the right order.
    const sourceModal =
      event.target instanceof Element
        ? /** @type {HTMLDialogElement | null} */ (event.target.closest('dialog:modal'))
        : null;

    event.promise
      ?.then(({ cart, detail }) => {
        if (detail?.didError) return;

        const present = () => this.#present({ cart, detail, addedVariantId });
        if (sourceModal && sourceModal.open && sourceModal !== this.#dialog) {
          sourceModal.addEventListener('close', present, { once: true });
        } else {
          present();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[add-to-cart-modal] event promise rejected:', error);
      });
  };

  /**
   * @param {{ cart: any, detail: any, addedVariantId: string }} args
   */
  #present({ cart, detail, addedVariantId }) {
    const items = Array.isArray(detail?.items) ? detail.items : [];
    if (items.length === 0) return;

    // Identify the just-added line. Prefer the added variant id (when the event
    // exposes it), then the added product id (product-form always resolves
    // `detail.productId`), then fall back to the last line.
    const productId = detail?.productId != null ? String(detail.productId) : '';
    let index = addedVariantId
      ? items.findIndex(/** @param {any} item */ (item) => String(item.variant_id) === addedVariantId)
      : -1;
    if (index < 0 && productId) {
      index = items.findIndex(/** @param {any} item */ (item) => String(item.product_id) === productId);
    }
    if (index < 0) index = items.length - 1;

    const item = items[index];
    if (!item) return;

    this.#lineIndex = index + 1;
    this.#trackedVariantId = String(item.variant_id ?? '');
    this.#fill(item, cart, detail);
    this.#open();
  }

  /**
   * @param {any} item - The just-added cart line item.
   * @param {any} cart
   * @param {any} detail
   */
  #fill(item, cart, detail) {
    const image = /** @type {HTMLImageElement | null} */ (this.querySelector('[data-at-cart-image]'));
    if (image) {
      const src = this.#imageUrl(item.featured_image || item.image, 720);
      if (src) {
        image.src = src;
        image.alt = item.product_title || item.title || '';
        image.hidden = false;
      } else {
        image.hidden = true;
      }
    }

    const name = this.querySelector('[data-at-cart-name]');
    if (name) name.textContent = item.product_title || item.title || '';

    const desc = /** @type {HTMLElement | null} */ (this.querySelector('[data-at-cart-desc]'));
    if (desc) {
      const text = this.#shortText(item.product_description, 20);
      desc.textContent = text;
      desc.hidden = text.length === 0;
    }

    const price = this.querySelector('[data-at-cart-price]');
    if (price) price.textContent = this.#money(item.final_line_price ?? item.price);

    const countEl = this.querySelector('.at-btn__count');
    if (countEl) countEl.textContent = String(item.quantity ?? 1);

    // "Go to My Cart (n)" = total units in the cart. detail.items is the full
    // cart, so summing quantities gives the count (detail.itemCount is only the
    // quantity just added).
    const total = Number(cart?.totalQuantity);
    const count = Number.isFinite(total) && total > 0 ? total : this.#sumQuantities(detail?.items);
    this.#updateCartCountLabel(count);
  }

  /**
   * Re-syncs the modal's totals after a quantity edit resolves: the
   * "Go to My Cart (n)" count and the tracked line's price. Called for
   * 'update'/'remove' events while the modal stays open.
   * @param {{ cart: any, detail: any }} args
   */
  #syncTotals({ cart, detail }) {
    const total = Number(cart?.totalQuantity);
    const count = Number.isFinite(total) && total > 0 ? total : this.#sumQuantities(detail?.items);
    this.#updateCartCountLabel(count);

    // Refresh the price of the line still shown in the modal. Match by variant
    // id (indices shift when a line is removed); leave the last price if the
    // tracked line is gone (its quantity reached 0).
    const items = Array.isArray(detail?.items) ? detail.items : [];
    const item = this.#trackedVariantId
      ? items.find(/** @param {any} i */ (i) => String(i.variant_id) === this.#trackedVariantId)
      : items[(this.#lineIndex ?? 0) - 1];

    const price = this.querySelector('[data-at-cart-price]');
    if (price && item) price.textContent = this.#money(item.final_line_price ?? item.price);
  }

  /**
   * Edits the just-added cart line, reusing the theme's cart machinery so the
   * drawer, totals and nav counter all stay in sync.
   * @param {number} quantity
   */
  #setLineQuantity(quantity) {
    if (!this.#lineIndex) return;

    const cartItems = /** @type {any} */ (document.querySelector('cart-items-component'));
    if (cartItems?.updateQuantity) {
      cartItems.updateQuantity({ line: this.#lineIndex, quantity, action: 'change' });
    }
  }

  /**
   * @param {number} count
   */
  #updateCartCountLabel(count) {
    const textEl = this.querySelector('.at-btn--p-accent .at-btn__text');
    if (textEl && Number.isFinite(count)) textEl.textContent = `Go to My Cart (${count})`;
  }

  /**
   * @param {Array<any> | undefined} items
   * @returns {number}
   */
  #sumQuantities(items) {
    if (!Array.isArray(items)) return 0;
    return items.reduce((total, item) => total + (Number(item.quantity) || 0), 0);
  }

  /**
   * Normalises a Shopify image field (URL string or object) to a sized CDN URL.
   * @param {any} field
   * @param {number} width
   * @returns {string}
   */
  #imageUrl(field, width) {
    let url = typeof field === 'string' ? field : field?.url || field?.src || '';
    if (url && url.includes('cdn.shopify.com') && !/[?&]width=/.test(url)) {
      url += `${url.includes('?') ? '&' : '?'}width=${width}`;
    }
    return url;
  }

  /**
   * Strips HTML and truncates to a word count.
   * @param {string | undefined} html
   * @param {number} words
   * @returns {string}
   */
  #shortText(html, words) {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    const text = (tmp.textContent || '').replace(/\s+/g, ' ').trim();
    const parts = text.split(' ');
    return parts.length > words ? `${parts.slice(0, words).join(' ')}…` : text;
  }

  /**
   * @param {number} cents
   * @returns {string}
   */
  #money(cents) {
    const currency = window.Shopify?.currency?.active || 'USD';
    const value = (Number(cents) || 0) / 100;
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(value);
    } catch {
      return `$${value.toFixed(2)}`;
    }
  }

  #open() {
    const dialog = this.#dialog;
    if (dialog && !dialog.open) dialog.showModal();
  }

  #close() {
    const dialog = this.#dialog;
    if (dialog?.open) dialog.close();
  }

  #openCartDrawer() {
    const drawer = /** @type {any} */ (document.getElementById('cart-drawer'));
    if (!drawer) return;

    customElements.whenDefined('theme-drawer').then(() => {
      if (typeof drawer.open === 'function') drawer.open();
    });
  }
}

if (!customElements.get('add-to-cart-modal')) {
  customElements.define('add-to-cart-modal', AddToCartModal);
}
